import { BellOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { Badge, Button, DatePicker, Empty, Popover, Space, Tabs, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import {
  formatNoticeDay,
  getPrintNotices,
  markPrintNoticesRead,
  type PrintNoticeItem,
  type PrintNotices,
} from '@/services/sukchaak';

export function NoticeDateBar({
  day,
  onChange,
}: {
  day: string;
  onChange: (next: string) => void;
}) {
  const value = dayjs(day);
  return (
    <Space size={4} wrap style={{ display: 'flex', width: '100%' }}>
      <Button size="small" icon={<LeftOutlined />} onClick={() => onChange(value.subtract(1, 'day').format('YYYY-MM-DD'))}>
        前一天
      </Button>
      <DatePicker
        size="small"
        value={value}
        allowClear={false}
        onChange={(next) => next && onChange(next.format('YYYY-MM-DD'))}
      />
      <Button size="small" icon={<RightOutlined />} onClick={() => onChange(value.add(1, 'day').format('YYYY-MM-DD'))}>
        後一天
      </Button>
      <Button size="small" type="link" onClick={() => onChange(dayjs().format('YYYY-MM-DD'))}>
        今天
      </Button>
    </Space>
  );
}

const PrintNotice: React.FC = () => {
  const [day, setDay] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [data, setData] = useState<PrintNotices>();

  const refresh = useCallback(async (nextDay = day) => {
    try {
      setData(await getPrintNotices(nextDay));
    } catch {
      setData(undefined);
    }
  }, [day]);

  useEffect(() => {
    refresh(day);
  }, [day, refresh]);

  const markRead = async (item?: PrintNoticeItem) => {
    try {
      setData(await markPrintNoticesRead(day, item ? [item.task_id] : []));
    } catch {
      refresh(day);
    }
  };

  const content = (
    <div style={{ width: 400, maxHeight: 460, overflow: 'auto' }}>
      <div style={{ marginBottom: 8 }}>
        <NoticeDateBar
          day={day}
          onChange={(next) => {
            setDay(next);
          }}
        />
      </div>
      <div style={{ marginBottom: 8, color: 'rgba(0,0,0,0.65)' }}>
        {formatNoticeDay(data?.date || day, data?.is_today)}的印練習
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
          這一天待印 {data?.total_count ?? 0} 條，未看 {data?.unread_count ?? 0} 條
        </div>
      </div>
      <Space style={{ marginBottom: 8 }}>
        <Button size="small" disabled={!data?.unread_count} onClick={() => markRead()}>
          這一天全部標為已看
        </Button>
        <Button size="small" type="link" onClick={() => history.push('/today')}>
          去今日任務
        </Button>
      </Space>
      <Tabs
        size="small"
        items={(data?.tabs || []).map((tab) => ({
          key: tab.category,
          label: `${tab.category_label}${tab.unread_count ? ` ${tab.unread_count}` : tab.count ? ` ${tab.count}` : ''}`,
          children:
            tab.groups.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="這一天沒有待印練習" />
            ) : (
              tab.groups.map((group) => (
                <div key={`${tab.category}-${group.group_id ?? 'none'}`} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{group.group_name}</div>
                  {group.items.map((item) => (
                    <button
                      key={item.task_id}
                      type="button"
                      onClick={() => markRead(item)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        borderLeft: item.read ? '3px solid #d9d9d9' : '3px solid #1677ff',
                        background: item.read ? '#fafafa' : '#e6f4ff',
                        padding: '8px 10px',
                        marginBottom: 6,
                        cursor: 'pointer',
                        opacity: item.read ? 0.72 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontWeight: item.read ? 400 : 600 }}>
                          {item.student_name} · {item.subject}
                        </span>
                        <Tag color={item.read ? 'default' : 'blue'}>{item.read ? '已看' : '未看'}</Tag>
                      </div>
                      <div>{item.title}</div>
                      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                        通知日 {data?.date || day} · 第 {item.day_index}/{item.day_total} 天 · 考試 {item.event_date}
                      </div>
                    </button>
                  ))}
                </div>
              ))
            ),
        }))}
      />
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      title="印練習通知"
      content={content}
      onOpenChange={(open) => {
        if (open) refresh(day);
      }}
    >
      <span style={{ display: 'inline-flex', padding: 4, cursor: 'pointer' }}>
        <Badge count={data?.badge_count || 0} size="small" overflowCount={99}>
          <BellOutlined style={{ fontSize: 16 }} />
        </Badge>
      </span>
    </Popover>
  );
};

export default PrintNotice;
